import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    CardHeader,
    CardSection,
    ConfirmationModal,
    useModal,
} from '@europrocurement/flexy-components';
import { DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import { useTheme } from '@mui/material';
import {
    sortByAddedAtAsc,
    useDossierDocumentsCtx,
} from '@europrocurement/l2d-modules/providers/DossierDocumentsProvider';
import {
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierDocumentSignContext,
} from '../../../providers';
import RequiredDocumentsHeading from '../RequiredDocumentsHeading/RequiredDocumentsHeading';
import RequiredDocumentsList from '../RequiredDocumentsList/RequiredDocumentsList';
import RequiredDocumentUploader from '../RequiredDocumentUploader/RequiredDocumentUploader';
import useDossierFile from '../../../hooks/useDossierFile';
import { DisplayedDocument } from '../../../providers/types';
import { useDocumentSign, useDocumentSignRender, useTransition } from '../../../hooks';

export interface RequiredDocumentsTabPros extends React.PropsWithChildren {
    source?: 'BACK' | 'FRONT';
}
const RequiredDocumentsTab: React.FunctionComponent<RequiredDocumentsTabPros> = function ({
    children,
    source = 'BACK',
}) {
    const [listItems, setListItems] = useState<Array<DisplayedDocument>>([]);
    const { dosDocs, getMissingDocumentsCountByCategory, getDossierDocumentsByCategory } =
        useDossierDocumentsCtx();

    const { palette } = useTheme();
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);
    const { canApplyTransition, applyTransition } = useTransition();
    const { modalActions } = useModal();
    const requiredMissingDocumentsCount = useRef(-1);
    const {
        handleAddFile,
        handleRemoveFile,
        handleRemoveDocument,
        handleShow,
        handleDownload,
        handleAdditionalUploads,
        handleMoveFile,
        handleSplitDosDoc,
        numberFilesChanged,
        submittingItemId,
    } = useDossierFile({
        typeDoc: DOC_TYPE_REQUIRED,
        source,
    });

    const isSubmitting = submittingItemId !== null;

    const { showSignedDocument } = useDocumentSignRender();
    const { handleSignSingleDocument } = useDocumentSign();
    const { getDossiersBySignedFileId, getSignDocItems } = useDossierDocumentSignContext();

    const handleSignSingleDocProxy = (label: string, fichierId: number, fileName?: string) => {
        if (!dossier) {
            return;
        }
        handleSignSingleDocument(
            dossier.id as number,
            dossier.prescripteurId as number,
            dossier.prescripteurLibelle as string,
            dossier.prescripteurEmail as string,
            label,
            fichierId,
            fileName,
        );
    };

    const handleDownloadSigned = (fichierId: number, fileName: string) => {
        showSignedDocument(fichierId, fileName, getDossiersBySignedFileId(fichierId));
    };

    useEffect(() => {
        const mergedDocuments = [
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_REQUIRED,
                dossierId: dossier?.id as number,
            }),
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_REQUIRED,
                additionalDocs: true,
                dossierId: dossier?.id as number,
            }),
        ];

        const requiredFichierIds = new Set(
            mergedDocuments.map((d) => d.fichierId).filter((id): id is number => id != null),
        );
        const signDocItems = getSignDocItems({ referenceFichierIds: requiredFichierIds });
        const allDocs = [...mergedDocuments, ...signDocItems].sort(sortByAddedAtAsc);

        if (source === 'FRONT') {
            // Remove available actions on documents card for the front
            setListItems(
                allDocs.map((document) => ({
                    ...document,
                    movable: false,
                    removableDoc: false,
                })),
            );
        } else {
            setListItems(allDocs);
        }

        requiredMissingDocumentsCount.current = getMissingDocumentsCountByCategory({
            categoryId: DOC_TYPE_REQUIRED,
            dossierId: dossier?.id as number,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dosDocs, getDossiersBySignedFileId, getSignDocItems]);

    const countTotal = listItems.reduce(
        (accumulator, doc) => (doc.hasType ? accumulator + 1 : accumulator),
        0,
    );

    const handleUpdateStatusDossierToCheck = async () => {
        if (!dossier) {
            return;
        }
        if (canApplyTransition(dossier, 'to-check') && dossier.stepId === 3) {
            await applyTransition(dossier, 'to-check');
            modalActions.close();
            refreshDossier();
        }
    };

    // Check if some files were updated and if the dossier status has a specific control
    // /!\ Do not work locally with a yarn not build (dbl render)
    useEffect(
        () => () => {
            if (
                source === 'FRONT' &&
                dossier &&
                canApplyTransition(dossier, 'to-check') &&
                dossier.stepId === 3
            ) {
                if (numberFilesChanged.current > 0 && requiredMissingDocumentsCount.current === 0) {
                    modalActions.call(
                        <ConfirmationModal
                            messages={{
                                question: 'Confirmation ?',
                                detail: {
                                    message:
                                        'Vous avez modifié des documents, souhaitez-vous passer le dossier en A contrôler ?',
                                    severity: 'info',
                                },
                                validation: 'Oui, j’ai terminé',
                                annulation: 'Non, je continue',
                            }}
                            actionOnValidation={handleUpdateStatusDossierToCheck}
                            actionOnCancellation={() => {
                                modalActions.close();
                            }}
                            color={palette.primary}
                        />,
                    );
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <CardSection
            sx={{ width: '100%' }}
            sxContent={{
                padding: '0 8px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px 0',
            }}
            header={
                source === 'FRONT' ? (
                    <RequiredDocumentsHeading
                        currentCount={countTotal - requiredMissingDocumentsCount.current}
                        missingDocumentsCount={requiredMissingDocumentsCount.current}
                        totalCount={countTotal}
                    />
                ) : (
                    <CardHeader
                        title="Documents requis"
                        titleSx={{
                            fontSize: '20px',
                            lineHeight: '28px',
                        }}
                        border={false}
                        headerSx={{
                            paddingX: '32px',
                            paddingBottom: '0.5em',
                        }}
                        sx={{
                            paddingX: 0,
                        }}
                        childrenSx={{ paddingX: 0 }}
                    />
                )
            }
        >
            <RequiredDocumentsList
                documents={listItems}
                onUpload={handleAddFile}
                onDeletion={handleRemoveFile}
                onDocDeletion={handleRemoveDocument}
                onDownload={handleDownload}
                onShow={handleShow}
                onSign={handleSignSingleDocProxy}
                onDownloadSigned={handleDownloadSigned}
                onMove={handleMoveFile}
                onSplitDosDoc={handleSplitDosDoc}
                isSubmitting={isSubmitting}
                submittingItemId={submittingItemId}
            />
            <RequiredDocumentUploader
                uploading={submittingItemId === 'uploader'}
                disabled={isSubmitting}
                onChange={handleAdditionalUploads}
            />
            {children}
        </CardSection>
    );
};

export default RequiredDocumentsTab;
